import { useState, useEffect, useCallback } from 'react';
import { clockApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LogIn, LogOut, Clock, AlertTriangle, CheckCircle, CalendarDays,
  Loader, RefreshCw, ChevronDown, ChevronUp, Timer
} from 'lucide-react';
import { Button } from '../components/ui/button';

function formatTime(isoStr) {
  if (!isoStr) return '--:--';
  return new Date(isoStr).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T12:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function hoursLabel(h) {
  if (h == null) return '--';
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}h ${mm.toString().padStart(2, '0')}m`;
}

const CLOCK_STATUS = {
  complete:    { label: 'Completo',    cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  in_progress: { label: 'En curso',   cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  incomplete:  { label: 'Incompleto', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
};

function getClockStatus(status) {
  return CLOCK_STATUS[status] || CLOCK_STATUS.incomplete;
}

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="text-4xl font-mono font-bold tracking-widest text-foreground tabular-nums">
      {now.toLocaleTimeString('es-ES')}
    </div>
  );
}

export default function WorkClockPage() {
  const { user } = useAuth();
  const [todayRecords, setTodayRecords] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  const [punchResult, setPunchResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [todayRes, histRes] = await Promise.all([
        clockApi.today(),
        clockApi.myHistory({ date_from: dateFrom, date_to: dateTo }),
      ]);
      setTodayRecords(todayRes.data);
      setHistory(histRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handlePunch = async () => {
    setPunching(true);
    setPunchResult(null);
    try {
      const res = await clockApi.punch({});
      setPunchResult({ success: true, ...res.data });
      await load();
    } catch (e) {
      setPunchResult({ success: false, error: e.response?.data?.detail || 'Error al fichar' });
    } finally {
      setPunching(false);
    }
  };

  // Determine current state
  const entries = todayRecords.filter(r => r.type === 'entry');
  const exits = todayRecords.filter(r => r.type === 'exit');
  const isIn = entries.length > exits.length;
  const totalHoursToday = exits.reduce((sum, r) => sum + (r.hours_worked || 0), 0);
  const dayComplete = entries.length > 0 && exits.length > 0 && totalHoursToday >= 8;
  const canPunch = !dayComplete;
  const hasPartialDay = entries.length > 0 && exits.length > 0;

  const lastEntry = entries[entries.length - 1];
  const lastExit = exits[exits.length - 1];

  const getStatusLabel = () => {
    if (dayComplete) return 'Jornada completada';
    if (hasPartialDay) return 'Jornada en curso (+8h pendiente)';
    if (isIn) return 'En teletrabajo';
    return 'Sin fichar';
  };

  const getStatusClass = () => {
    if (dayComplete) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    if (isIn) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    return 'bg-muted text-muted-foreground';
  };

  const getStatusDot = () => {
    if (dayComplete) return 'bg-green-500';
    if (isIn) return 'bg-blue-500 animate-pulse';
    return 'bg-gray-400';
  };

  // Live hours counter when clocked in
  const [liveHours, setLiveHours] = useState(null);
  useEffect(() => {
    if (!isIn || !lastEntry) { setLiveHours(null); return; }
    const tick = () => {
      const diff = (Date.now() - new Date(lastEntry.timestamp)) / 3600000;
      setLiveHours(diff);
    };
    tick();
    const t = setInterval(tick, 10000);
    return () => clearInterval(t);
  }, [isIn, lastEntry]);

  // Weekly total
  const weekTotal = history.reduce((sum, d) => {
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
    if (new Date(d.date + 'T12:00') >= weekAgo) return sum + (d.total_hours || 0);
    return sum;
  }, 0);
  const monthTotal = history.reduce((sum, d) => sum + (d.total_hours || 0), 0);

  return (
    <div className="max-w-3xl space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
          <Clock className="h-6 w-6 text-brand" />
          Fichaje de Teletrabajo
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          {formatDate(new Date().toISOString().split('T')[0])}
        </p>
      </div>

      {/* Clock widget */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card border border-border rounded-2xl p-8 shadow-sm text-center"
      >
        <LiveClock />
        <p className="text-xs text-muted-foreground mt-1 mb-6">Hora local</p>

        {/* State indicator */}
        <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-6 ${getStatusClass()}`}>
          <span className={`h-2 w-2 rounded-full ${getStatusDot()}`} />
          {getStatusLabel()}
        </div>

        {/* Live timer */}
        {isIn && liveHours != null && (
          <div className="mb-4 flex items-center justify-center gap-2 text-blue-600 dark:text-blue-400">
            <Timer className="h-4 w-4 animate-pulse" />
            <span className="text-lg font-mono font-bold">{hoursLabel(liveHours)}</span>
            <span className="text-sm">trabajadas</span>
            {liveHours < 7 && (
              <span className="text-xs text-amber-600 dark:text-amber-400 ml-1">
                (min 7h recomendadas)
              </span>
            )}
          </div>
        )}

        {/* Punch button */}
        {canPunch && (
          <motion.button
            onClick={handlePunch}
            disabled={punching}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            data-testid="btn-punch"
            className={`relative mx-auto flex items-center justify-center gap-3 px-10 py-4 rounded-2xl text-white font-bold text-lg shadow-lg transition-all
              ${isIn
                ? 'bg-gradient-to-br from-orange-400 to-red-500 hover:from-orange-500 hover:to-red-600 shadow-red-200 dark:shadow-red-900/30'
                : 'bg-gradient-to-br from-brand to-blue-600 hover:from-brand hover:to-blue-700 shadow-blue-200 dark:shadow-blue-900/30'
              }`}
          >
            {punching ? (
              <Loader className="h-5 w-5 animate-spin" />
            ) : isIn ? (
              <LogOut className="h-5 w-5" />
            ) : (
              <LogIn className="h-5 w-5" />
            )}
            {punching ? 'Registrando...' : isIn ? 'Registrar Salida' : 'Registrar Entrada'}          </motion.button>
        )}

        {dayComplete && (
          <div className="mt-2 text-sm text-muted-foreground text-center">
            Has completado tu jornada de hoy ({totalHoursToday.toFixed(1)}h registradas).
          </div>
        )}
        {!dayComplete && entries.length > 0 && exits.length > 0 && (
          <div className="mt-2 text-sm text-amber-600 dark:text-amber-400 text-center">
            Llevas {totalHoursToday.toFixed(1)}h registradas. Puedes continuar fichando hasta completar 8h.
          </div>
        )}

        {/* Punch result */}
        <AnimatePresence>
          {punchResult && (
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mt-4 space-y-2"
            >
              {punchResult.success ? (
                <>
                  <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-400 text-sm font-medium">
                    <CheckCircle className="h-4 w-4" />
                    {punchResult.message}
                  </div>
                  {punchResult.warnings?.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-xs text-amber-700 dark:text-amber-300 text-left">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      {w}
                    </div>
                  ))}
                </>
              ) : (
                <div className="flex items-center justify-center gap-2 text-destructive text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  {punchResult.error}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Today timeline */}
      {todayRecords.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="bg-card border border-border rounded-2xl p-5 shadow-sm"
        >
          <h2 className="text-sm font-semibold font-heading mb-3 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-brand" />
            Fichajes de hoy
          </h2>
          <div className="space-y-2">
            {todayRecords.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3" data-testid={`clock-record-${r.id}`}>
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                  r.type === 'entry'
                    ? 'bg-blue-100 dark:bg-blue-900/30'
                    : 'bg-orange-100 dark:bg-orange-900/30'
                }`}>
                  {r.type === 'entry'
                    ? <LogIn className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    : <LogOut className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                  }
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">
                    {r.type === 'entry' ? 'Entrada' : 'Salida'} — {formatTime(r.timestamp)}
                  </div>
                  {r.hours_worked != null && (
                    <div className="text-xs text-muted-foreground">{hoursLabel(r.hours_worked)} trabajadas</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {lastExit?.hours_worked != null && (
            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Total de hoy</span>
              <span className="font-bold text-foreground">{hoursLabel(lastExit.hours_worked)}</span>
            </div>
          )}
        </motion.div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Esta semana', value: hoursLabel(weekTotal), sub: `${history.filter(d => { const w = new Date(); w.setDate(w.getDate() - 7); return new Date(d.date + 'T12:00') >= w; }).length} ${history.filter(d => { const w = new Date(); w.setDate(w.getDate() - 7); return new Date(d.date + 'T12:00') >= w; }).length === 1 ? 'día' : 'días'}` },
          { label: 'Este mes', value: hoursLabel(monthTotal), sub: `${history.length} ${history.length === 1 ? 'día' : 'días'}` },
        ].map(stat => (
          <div key={stat.label} className="bg-card border border-border rounded-xl p-4 shadow-sm text-center">
            <div className="text-2xl font-bold font-heading text-foreground">{stat.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{stat.label}</div>
            <div className="text-xs text-brand mt-0.5">{stat.sub}</div>
          </div>
        ))}
      </div>

      {/* History */}
      <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted/40 transition-colors"
          onClick={() => setShowHistory(h => !h)}
          data-testid="toggle-history"
        >
          <span className="text-sm font-semibold font-heading">Historial de fichajes</span>
          {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        <AnimatePresence>
          {showHistory && (
            <motion.div
              initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
              className="overflow-hidden"
            >
              <div className="px-5 pb-2 pt-1 border-t border-border">
                <div className="flex gap-2 mb-3 items-center">
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                    className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground" />
                  <span className="text-xs text-muted-foreground">—</span>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                    className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground" />
                  <Button size="sm" variant="outline" onClick={load} className="h-7 px-2">
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {loading ? (
                  <div className="py-6 text-center"><Loader className="h-5 w-5 animate-spin mx-auto text-brand" /></div>
                ) : history.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">Sin registros en ese período</div>
                ) : (
                  <div className="space-y-2 pb-2" data-testid="history-list">
                    {history.map(day => (
                      <div key={day.date} className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
                        <div className="text-sm font-medium capitalize">{formatDate(day.date)}</div>
                        <div className={`text-xs font-medium px-2 py-0.5 rounded-full ${getClockStatus(day.status).cls}`}>
                          {getClockStatus(day.status).label}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
